import clsx from "clsx";
import { useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import useGetActorSummary from "../../../hooks/useGetActorSummary";
import { AllRowsSelector, Grid, RLCard } from "../../../../../components";
import AIGeneratedIcon from "../../../../../components/icons/AIGeneratedIcon";
import ClearButton from "../../../../search/components/ClearButton";
import ActorIOCExportDropdown from "./ActorIOCExportDropdown";
import { ActorIOCSchema } from "../ActorIOCSchema";
import { usePagination, useSelectRowsContext } from "../../../../../hooks";
import { sortGridByColumn } from "../../../../../utils";
import { createSelectedRowKey, isEmpty } from "../../../util";
import { IOC_TYPES_MAP } from "../consts";
import { ActorIOCProps, IOC, IOCTypeFilterOption } from "../../../@types/actor";
import { SortParams } from "../../../@types/rldynamicanalysis/yara";
import styles from "../ioc.module.scss";

const typeFilterPlaceholder = (
  <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <div className={clsx("icon-filter", "pt-5")} />
    <div>Type</div>
  </span>
);

const typeFilterOptions: IOCTypeFilterOption[] = Object.entries(
  IOC_TYPES_MAP
).reduce<IOCTypeFilterOption[]>((acc, [key, label]) => {
  if (!acc.find(opt => opt.label === label)) {
    acc.push({ value: key, label });
  }
  return acc;
}, []);

const filterBySearchValue = (item: IOC, value: string) =>
  value ? item.value.toLowerCase().includes(value.toLowerCase()) : true;

const filterByType = (item: IOC, typeLabel: string) => {
  if (!typeLabel) return true;
  const mappedLabel = IOC_TYPES_MAP[item.type as keyof typeof IOC_TYPES_MAP];
  return mappedLabel === typeLabel || item.type === typeLabel;
};

const ActorIOC = ({ actorName, iocs }: ActorIOCProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { data: actorData } = useGetActorSummary(!iocs ? actorName || "" : "");
  const actorRelatedIOC = useMemo(
    () => iocs || actorData?.iocs || [],
    [iocs, actorData?.iocs]
  );

  const [searchValue, setSearchValue] = useState("");
  const [filterType, setFilterType] = useState("");
  const [selectedType, setSelectedType] = useState<IOCTypeFilterOption | null>(
    null
  );

  const filteredData = useMemo(
    () =>
      actorRelatedIOC
        .filter((item: IOC) => filterBySearchValue(item, searchValue))
        .filter((item: IOC) => filterByType(item, filterType)),
    [actorRelatedIOC, searchValue, filterType]
  );

  const { data, currentPageData, updatePageData } = usePagination(
    filteredData,
    { pageSize: 10 }
  );

  const [sort, setSort] = useState({
    field: "default",
    direction: "asc"
  });

  const { isSelectAll, api, selected } = useSelectRowsContext();

  const handlePageChange = (newPage: number, newPageSize?: number): void => {
    updatePageData({ newPage, newPageSize });
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSortChange = (sortParams: SortParams) => {
    const { field, direction } = sort;
    if (sortParams.direction === direction && sortParams.field === field)
      return;
    setSort(sortParams);
    sortGridByColumn(data, sortParams);
  };

  return (
    <RLCard
      header={
        <RLCard.Header>
          <div className="flex flex-center flex-gap-2">
            <span>Actor Related IOCs</span>
            <AIGeneratedIcon />
          </div>
        </RLCard.Header>
      }
      content={
        <RLCard.Content>
          {actorRelatedIOC.length > 0 ? (
            <div ref={gridRef}>
              <div
                className="flex flex-center flex-gap-4"
                style={{ paddingLeft: "20px" }}
              >
                <div className={styles.ioc_filter_container}>
                  <Form.Control
                    className={styles.ioc_search_filter}
                    type="text"
                    placeholder="Search"
                    value={searchValue}
                    onChange={event => {
                      setSearchValue(event.target.value);
                      updatePageData({ newPage: 1 });
                      api.clear();
                    }}
                    data-testid="actor_ioc_search_input"
                  />
                  <span
                    className={clsx("icon-search", styles.ioc_search_icon)}
                  />
                </div>
                <Select<IOCTypeFilterOption>
                  options={typeFilterOptions}
                  isClearable
                  placeholder={typeFilterPlaceholder}
                  value={selectedType}
                  onChange={option => {
                    setSelectedType(option);
                    setFilterType(option?.label || "");
                    updatePageData({ newPage: 1 });
                    api.clear();
                  }}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    container: base => ({ ...base, width: "200px" })
                  }}
                  data-testid="actor_ioc_type_filter"
                />
                <ClearButton
                  label="Clear"
                  className={styles.ioc_clear_button}
                  disabled={!searchValue && !filterType}
                  onClick={() => {
                    setSearchValue("");
                    setFilterType("");
                    setSelectedType(null);
                    updatePageData({ newPage: 1 });
                  }}
                />
                <div className="ms-auto">
                  <ActorIOCExportDropdown
                    pageData={data}
                    selectedRows={selected}
                    actorName={actorName}
                  />
                </div>
              </div>
              {!isEmpty(selected) && (
                <AllRowsSelector
                  allRowsSelected={isSelectAll}
                  totalResults={filteredData.length}
                  selected={Object.values(selected)}
                  onSelectAllRowsChange={() =>
                    api.selectAll(filteredData, currentPageData.page ?? 1)
                  }
                  onDeSelectAllRowsChange={() => api.clear()}
                  entityName="IOC"
                />
              )}
              <Grid
                schema={ActorIOCSchema}
                data={data}
                totalResults={filteredData.length}
                page={currentPageData.page}
                pageSize={currentPageData.pageSize}
                onPageChange={handlePageChange}
                numPages={currentPageData.numPages}
                showPageNumbers
                sort={sort}
                onSortChange={handleSortChange}
                enablePageSizeSelector
                pageSizeSelectorItems={[
                  { label: "10", value: "10" },
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                  { label: "100", value: "100" },
                  { label: "200", value: "200" }
                ]}
                selectableRows
                selectedRows={selected}
                onRowSelectChange={(row: IOC, index: number) =>
                  api.select(row, index, currentPageData.page ?? 1)
                }
                checkRowSelected={(row: IOC, index: number) =>
                  api.checkIsSelected(row, index, currentPageData.page ?? 1)
                }
                onSelectAllRowsChange={() =>
                  api.selectPage(data, currentPageData.page ?? 1)
                }
                createSelectedRowKey={createSelectedRowKey}
                allRowsSelected={api.isPageSelected(
                  data,
                  currentPageData.page ?? 1
                )}
              />
            </div>
          ) : (
            <span> No related IOC data available for this actor.</span>
          )}
        </RLCard.Content>
      }
    />
  );
};

export default ActorIOC;
